import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/utils/supabase";
import { redirectToExternalUrl } from "@/utils/externalRedirect";

const COUNTDOWN_SECONDS = 10 * 60; // 10分钟倒计时

export default function PayPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const name = searchParams.get("name") || "";
  const gender = searchParams.get("gender") || "";
  const birthday = searchParams.get("birthday") || "";
  const orderNoFromQuery = searchParams.get("order_no") || "";

  // 调试日志
  useEffect(() => {
    console.log("[PayPage] URL参数:", { name, gender, birthday, rawUrl: typeof window !== 'undefined' ? window.location.href : 'N/A' });
  }, [name, gender, birthday]);

  // 从 localStorage 读取支付链接、企业微信链接和金额
  const [payUrl, setPayUrl] = useState<string>("");
  const [wechatLink, setWechatLink] = useState<string>("");
  const [amount, setAmount] = useState<number>(9.9);
  const [orderNo, setOrderNo] = useState<string>("");

  const [seconds, setSeconds] = useState(COUNTDOWN_SECONDS);
  const [paying, setPaying] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // 轮询订单状态的定时器
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // 从 localStorage 读取支付链接、企业微信链接和金额
    const storedPayUrl = localStorage.getItem("pay_url") || "";
    const storedWechatLink = localStorage.getItem("wechat_link") || "";
    const storedAmount = localStorage.getItem("pay_amount") || "9.9";
    const storedOrderNo = localStorage.getItem("pay_order_no") || "";
    const resolvedOrderNo = storedOrderNo || orderNoFromQuery;

    setPayUrl(storedPayUrl);
    setWechatLink(storedWechatLink);
    setAmount(parseFloat(storedAmount) || 9.9);
    setOrderNo(resolvedOrderNo);

    if (!storedOrderNo && resolvedOrderNo) {
      localStorage.setItem("pay_order_no", resolvedOrderNo);
    }

    intervalRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(intervalRef.current!);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [orderNoFromQuery]);

  // 轮询订单支付状态 - 一旦检测到支付成功，立刻跳转到企V
  useEffect(() => {
    if (!orderNo) return;

    const checkOrderStatus = async () => {
      try {
        const { data, error } = await supabase
          .from("orders")
          .select("status, wechat_link")
          .eq("order_no", orderNo)
          .maybeSingle();

        if (error) {
          console.error("[PayPage] 轮询订单失败:", error);
          return;
        }

        if (data?.status === "paid") {
          console.log("[PayPage] 检测到订单已支付，准备跳转企V");
          // 停止轮询
          if (pollRef.current) clearInterval(pollRef.current);

          // 优先使用订单中的企V链接，其次用 localStorage
          const targetLink = data?.wechat_link || wechatLink || localStorage.getItem("wechat_link") || "";
          if (targetLink) {
            console.log("[PayPage] 跳转到企V:", targetLink);
            redirectToExternalUrl(targetLink);
          } else {
            // 没有企V链接，跳转到 success 页面
            navigate(`/success?order_no=${orderNo}`);
          }
        }
      } catch (err) {
        console.error("[PayPage] 轮询异常:", err);
      }
    };

    // 立即检查一次
    checkOrderStatus();
    // 每3秒轮询一次
    pollRef.current = setInterval(checkOrderStatus, 3000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [orderNo, wechatLink, navigate]);

  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  const handlePay = () => {
    if (!payUrl) {
      alert("支付链接无效，请返回重新测算");
      return;
    }
    setPaying(true);
    // 企业微信链接已经在 localStorage 中了
    // 直接在当前窗口跳转，更可靠
    console.log("[Pay] 跳转支付链接:", payUrl);
    redirectToExternalUrl(payUrl);
  };

  // 计算原价（假设原价是金额的17倍左右，即9.9对应168）
  const originalPrice = Math.round(amount * 17);

  return (
    <div
      className="min-h-screen w-full"
      style={{
        background: "#c0000a",
        fontFamily: "'Noto Serif SC', serif",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+SC:wght@400;600;700&display=swap');
        @keyframes shimmerBar {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        @keyframes countFlash {
          0%, 100% { color: #e8b84b; }
          50% { color: #fff; }
        }
        .shimmer-gold {
          background: linear-gradient(90deg, #c8922a 0%, #e8b84b 40%, #fff8e1 50%, #e8b84b 60%, #c8922a 100%);
          background-size: 200% auto;
          animation: shimmerBar 1.8s linear infinite;
        }
        .fade-up { animation: fadeUp 0.5s ease both; }
        .count-flash { animation: countFlash 1s ease-in-out infinite; }
        .pay-btn-wx {
          background: linear-gradient(135deg, #07c160 0%, #09d96a 100%);
          box-shadow: 0 4px 20px rgba(7,193,96,0.4);
        }
        .pay-btn-wx:active { transform: scale(0.98); }
        .pay-btn-ali {
          background: linear-gradient(135deg, #1677ff 0%, #4096ff 100%);
          box-shadow: 0 4px 20px rgba(22,119,255,0.4);
        }
        .pay-btn-ali:active { transform: scale(0.98); }
      `}</style>

      {/* Banner 图 */}
      <div className="w-full relative overflow-hidden" style={{ height: 200 }}>
        <img
          src="https://readdy.ai/api/search-image?query=Chinese%20traditional%20wedding%20fortune%20telling%20banner%20with%20red%20background%2C%20golden%20Chinese%20calligraphy%20text%2C%20mandarin%20ducks%2C%20red%20lanterns%2C%20plum%20blossoms%2C%20festive%20auspicious%20decorations%2C%20rich%20ornate%20style%2C%20warm%20red%20and%20gold%20color%20palette%2C%20horizontal%20banner%20format&width=800&height=200&seq=pay-banner-001&orientation=landscape"
          alt="姻缘测算"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.15)" }} />
        <div className="absolute top-2 left-0 right-0 text-center">
          <p className="text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>内容纯属娱乐，暂不向未满18岁青少年提供</p>
        </div>
      </div>

      {/* 主内容区 */}
      <div className="px-4 py-4 space-y-3 fade-up">

        {/* 用户信息卡 */}
        <div
          className="w-full rounded-xl px-4 py-3"
          style={{
            background: "linear-gradient(135deg, #fdf3e3 0%, #f5e6c8 100%)",
            border: "1px solid #d4a017",
          }}
        >
          <div className="flex items-center justify-between">
            <span className="text-sm" style={{ color: "#5a1a00" }}>
              姓名：<strong>{name || "—"}</strong>
            </span>
            <span className="text-sm" style={{ color: "#5a1a00" }}>
              性别：<strong>{gender || "—"}</strong>
            </span>
          </div>
          <div className="mt-1.5">
            <span className="text-sm" style={{ color: "#5a1a00" }}>
              出生：<strong style={{ fontSize: 12 }}>{birthday || "—"}</strong>
            </span>
          </div>
        </div>

        {/* 报告预览卡 */}
        <div
          className="w-full rounded-xl overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #fdf3e3 0%, #f5e6c8 100%)",
            border: "1px solid #d4a017",
          }}
        >
          {/* 标题 */}
          <div
            className="px-4 py-2.5 flex items-center gap-2"
            style={{ borderBottom: "1px solid rgba(212,160,23,0.3)" }}
          >
            <span style={{ fontSize: 16 }}>📜</span>
            <span className="text-sm font-bold" style={{ color: "#8b1a1a" }}>您的专属姻缘报告已生成</span>
          </div>

          {/* 模糊预览内容 */}
          <div className="px-4 py-3 relative">
            <div className="space-y-2">
              {[
                "您一生将有 ▓▓ 段婚姻缘分...",
                "您的桃花运最旺时期在 ▓▓▓▓ 年...",
                "您的姻缘贵人属相为 ▓▓，方位在 ▓▓...",
                "您与 ▓▓ 属相最为相配，感情稳定...",
                "您的婚姻宫位显示 ▓▓▓▓▓▓▓▓...",
              ].map((line, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span style={{ color: "#c8922a", fontSize: 12, marginTop: 2 }}>✦</span>
                  <p
                    className="text-sm flex-1"
                    style={{
                      color: "#5a3a10",
                      filter: i >= 1 ? "blur(4px)" : "none",
                      userSelect: "none",
                    }}
                  >
                    {line}
                  </p>
                </div>
              ))}
            </div>

            {/* 遮罩 */}
            <div
              className="absolute inset-x-0 bottom-0 flex items-end justify-center pb-3"
              style={{
                height: "70%",
                background: "linear-gradient(to bottom, transparent 0%, rgba(253,243,227,0.95) 60%)",
              }}
            >
              <div className="text-center">
                <div
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold"
                  style={{ background: "rgba(192,0,10,0.1)", color: "#c0000a", border: "1px solid rgba(192,0,10,0.2)" }}
                >
                  <i className="ri-lock-line" style={{ fontSize: 11 }} />
                  付费后解锁完整报告
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 价格区 */}
        <div
          className="w-full rounded-xl px-4 py-3"
          style={{
            background: "linear-gradient(135deg, #fdf3e3 0%, #f5e6c8 100%)",
            border: "1px solid #d4a017",
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span
                className="px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap"
                style={{ background: "#c0000a", color: "#fff" }}
              >
                限时优惠
              </span>
              <span className="text-2xl font-bold" style={{ color: "#c0000a" }}>¥{amount}元</span>
            </div>
            <div className="text-right">
              <p className="text-xs" style={{ color: "#999" }}>原价：<s>¥{originalPrice}元</s></p>
              <p className="text-xs" style={{ color: "#999" }}>距优惠结束</p>
            </div>
          </div>

          {/* 倒计时 */}
          <div className="flex items-center justify-between mt-2.5">
            <p className="text-xs" style={{ color: "#8b5e1a" }}>报告生成后，只有您自己能查看，请放心领取！</p>
            <div
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg flex-shrink-0"
              style={{ background: "rgba(192,0,10,0.08)", border: "1px solid rgba(192,0,10,0.2)" }}
            >
              <i className="ri-time-line" style={{ color: "#c0000a", fontSize: 12 }} />
              <span
                className="text-sm font-bold tabular-nums count-flash"
                style={{ color: "#c0000a", minWidth: 42 }}
              >
                {mm}:{ss}
              </span>
            </div>
          </div>
        </div>

        {/* 支付按钮区 */}
        <div className="space-y-2.5 pb-6">
          {payUrl ? (
            <>
              {/* 微信支付 */}
              <button
                type="button"
                onClick={handlePay}
                disabled={paying || seconds === 0}
                className="pay-btn-wx w-full py-4 rounded-xl flex items-center justify-center gap-3 cursor-pointer whitespace-nowrap transition-all"
                style={{ border: "none", opacity: seconds === 0 ? 0.5 : 1 }}
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-wechat-pay-fill text-white" style={{ fontSize: 22 }} />
                </div>
                <span className="text-base font-bold text-white" style={{ letterSpacing: "0.05em" }}>
                  {paying ? "跳转中..." : `微信支付 ¥${amount}元`}
                </span>
              </button>

              {/* 倒计时到期提示 */}
              {seconds === 0 && (
                <div
                  className="w-full py-3 rounded-xl text-center text-sm font-bold"
                  style={{ background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }}
                >
                  优惠已过期，请重新测算
                </div>
              )}
            </>
          ) : (
            /* 支付接口异常时，显示企业微信联系方式 */
            <div
              className="w-full rounded-xl px-4 py-5 text-center space-y-3"
              style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.25)" }}
            >
              <div className="w-10 h-10 flex items-center justify-center mx-auto rounded-full" style={{ background: "rgba(255,255,255,0.15)" }}>
                <i className="ri-customer-service-2-line" style={{ color: "#fff", fontSize: 20 }} />
              </div>
              <p className="text-sm font-bold" style={{ color: "#fff" }}>支付通道加载中</p>
              <p className="text-xs" style={{ color: "rgba(255,255,255,0.7)" }}>
                请添加客服微信，发送您的姓名和出生日期，即可人工完成支付
              </p>
              {wechatLink ? (
                <a
                  href={wechatLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap"
                  style={{ background: "#07c160", color: "#fff", textDecoration: "none", boxShadow: "0 4px 16px rgba(7,193,96,0.4)" }}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-wechat-fill" style={{ fontSize: 16 }} />
                  </div>
                  联系客服完成支付
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate("/")}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm cursor-pointer whitespace-nowrap"
                  style={{ background: "rgba(255,255,255,0.2)", color: "#fff", border: "none" }}
                >
                  返回重新测算
                </button>
              )}
            </div>
          )}

          {/* 安全提示 */}
          <div className="flex items-center justify-center gap-4 pt-1">
            {[
              { icon: "ri-shield-check-line", text: "安全支付" },
              { icon: "ri-lock-2-line", text: "隐私保护" },
              { icon: "ri-customer-service-line", text: "售后保障" },
            ].map((item) => (
              <div key={item.text} className="flex items-center gap-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={item.icon} style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }} />
                </div>
                <span className="text-xs" style={{ color: "rgba(255,255,255,0.5)" }}>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
